import React, { useState, useRef, useEffect } from "react";
import { Table } from "reactstrap";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useTranslation } from "react-i18next";
import { HexColorPicker } from "react-colorful";
import DraggableTagRow from "./DraggableTagRow";
import { Tooltip } from "react-tooltip";
import { getLastTagColor } from "src/utils/common";

const TagsManageTable = ({
  tags,
  onMoveTag,
  onDeleteTag,
  isAddingNewRow,
  newTagName,
  onNewTagNameChange,
  onSaveNewRow,
  onCancelNewRow,
  showValidationError,
  errorMessage,
  newRowRef,
  tableContainerRef,
  newTagColor,
  onNewTagColorChange,
}) => {
  const { t } = useTranslation("test-package", "common");
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [colorPickerPosition, setColorPickerPosition] = useState({ top: 0, left: 0 });
  const colorPickerRef = useRef(null);
  const colorButtonRef = useRef(null);

  const getNextRowNumber = () => tags.length + 1;
  const getCurrentTagColor = () => newTagColor || getLastTagColor(tags);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Only close if clicking outside both the color picker and the button
      if (
        showColorPicker &&
        colorPickerRef.current &&
        !colorPickerRef.current.contains(event.target) &&
        colorButtonRef.current &&
        !colorButtonRef.current.contains(event.target)
      ) {
        setShowColorPicker(false);
      }
    };

    const handleResize = () => {
      if (showColorPicker) {
        updateColorPickerPosition();
      }
    };

    // Use a small delay to prevent immediate closure when opening
    if (showColorPicker) {
      const timeoutId = setTimeout(() => {
        document.addEventListener("mousedown", handleClickOutside);
        window.addEventListener("resize", handleResize);
      }, 100);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener("mousedown", handleClickOutside);
        window.removeEventListener("resize", handleResize);
      };
    }
  }, [showColorPicker]);

  const updateColorPickerPosition = () => {
    if (colorButtonRef.current) {
      const rect = colorButtonRef.current.getBoundingClientRect();

      setColorPickerPosition({
        top: rect.bottom + 5,
        left: rect.left - 100, // Center the picker relative to button
      });
    }
  };

  const handleColorButtonClick = (e) => {
    e.stopPropagation();

    updateColorPickerPosition();
    setShowColorPicker((prev) => !prev);
  };

  const handleColorChange = (color) => {
    if (onNewTagColorChange) {
      onNewTagColorChange(color);
    }
  };

  return (
    <>
      <DndProvider backend={HTML5Backend}>
        <div ref={tableContainerRef} className="tags-table-container max-height-by-px-320 overflow-y-auto">
          <Table className="table-tags-manage mb-0">
            <thead className="sticky-top">
              <tr>
                <th className="width-by-px-60 border-0 fw-bold text-center align-middle">#</th>
                <th className="width-by-px-80 border-0 fw-bold text-center align-middle">{t("list.tag_modal.color")}</th>
                <th className="border-0 fw-bold text-nowrap text-left align-middle">{t("list.tag_modal.step_or_tag_name")}</th>
                <th className="width-by-px-80 border-0 fw-bold text-center align-middle"></th>
              </tr>
            </thead>
            <tbody>
              {tags.map((tag, index) => (
                <DraggableTagRow key={tag?.display_order} tag={tag} index={index} moveTag={onMoveTag} onDelete={onDeleteTag} />
              ))}
              {isAddingNewRow && (
                <tr ref={newRowRef} className="new-tag-row">
                  <td className="width-by-px-60 text-center important-p-y-24">
                    <span>{getNextRowNumber()}</span>
                  </td>
                  <td className="width-by-px-80 text-center important-p-y-24">
                    <div className="d-flex justify-content-center align-items-center">
                      <button
                        ref={colorButtonRef}
                        type="button"
                        className="border-0 p-0 rounded-circle width-by-px-24 height-by-px-24"
                        onClick={handleColorButtonClick}
                        style={{
                          backgroundColor: getCurrentTagColor(),
                          cursor: "pointer",
                        }}
                        data-tooltip-id="tooltip-test-sequence-tag-new-color"
                        data-tooltip-content={t("change_color")}
                        title={t("list.tag_modal.select_color", "Select color")}
                      />
                      <Tooltip
                        id="tooltip-test-sequence-tag-new-color"
                        className="text-break-space max-width-by-px-300"
                        place="top"
                        positionStrategy="fixed"
                        style={{ zIndex: 1080 }}
                      />
                    </div>
                  </td>
                  <td className="important-p-y-24">
                    <div className="position-relative">
                      <input
                        type="text"
                        className={`form-control form-control-sm ${showValidationError ? "border-danger" : ""}`}
                        placeholder={t("list.tag_modal.add_tag_placeholder")}
                        value={newTagName}
                        onChange={(e) => onNewTagNameChange(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            onSaveNewRow();
                          }
                        }}
                        autoFocus
                      />
                      {showValidationError && errorMessage && <div className="position-absolute text-danger small">{errorMessage}</div>}
                    </div>
                  </td>
                  <td className="width-by-px-80 text-center important-p-y-24">
                    <div className="d-flex justify-content-center align-items-center">
                      <button
                        data-tooltip-id="add-new-tag"
                        data-tooltip-content={t("list.tag_modal.add_new_tag")}
                        type="button"
                        className="btn btn-sm btn-success me-1 px-2"
                        onClick={onSaveNewRow}
                        disabled={!newTagName}
                      >
                        ✓
                      </button>
                      <Tooltip
                        id="add-new-tag"
                        className="text-break-space max-width-by-px-300"
                        place="top"
                        positionStrategy="fixed"
                        style={{ zIndex: 1080 }}
                      />
                      <button
                        data-tooltip-id="cancel-add-new-tag"
                        data-tooltip-content={t("cancel")}
                        type="button"
                        className="btn btn-sm btn-secondary px-2"
                        onClick={onCancelNewRow}
                        title={t("cancel")}
                      >
                        ✕
                      </button>
                      <Tooltip
                        id="cancel-add-new-tag"
                        className="text-break-space max-width-by-px-300"
                        place="top"
                        positionStrategy="fixed"
                        style={{ zIndex: 1080 }}
                      />
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </DndProvider>

      {showColorPicker && (
        <div
          ref={colorPickerRef}
          className="color-picker-container"
          style={{
            top: `${colorPickerPosition.top}px`,
            left: `${colorPickerPosition.left}px`,
          }}
        >
          <HexColorPicker color={getCurrentTagColor()} onChange={handleColorChange} />
        </div>
      )}
    </>
  );
};

export default TagsManageTable;
